import { navigateLeftStack, store } from '@/app/store';
import config from '@/config';
import { setGroupParticipants } from '@/features/chats/model/groupParticipants';
import { formatUserIdToJid } from '@/features/chats/utils';
import { getUserNickName } from '@/features/users';
import { addParticipants, createGroup } from '@/infrastructure/sdk';
import { blockOfflineAction, logger } from '@/shared/utils';
import { showErrorToast, showSuccessToast } from '@/shared/utils/toastMessage';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLanguage } from '@/app/providers';

/**
 * Reusable hook for managing group participants (creation or editing).
 */
export const useAddParticipants = ({ groupId, groupName, groupImage, minCount, maxCount, onClose } = {}) => {
   const { t } = useLanguage();
   const [selectedUsers, setSelectedUsers] = useState([]);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const dispatch = useDispatch();

   const selectedCount = selectedUsers.length;
   const isCreateMode = !groupId;

   const handleToggleUser = useCallback(
      userId => {
         setSelectedUsers(prev => {
            if (prev.includes(userId)) {
               return prev.filter(id => id !== userId);
            }
            if (maxCount && prev.length >= maxCount) {
               return prev;
            }
            return [...prev, userId];
         });
      },
      [maxCount],
   );

   const handleCreateGroup = useCallback(async () => {
      if (blockOfflineAction()) return;
      if (selectedCount < (minCount || config.minGroupMembers) || !groupName) return;

      setIsSubmitting(true);
      try {
         const memberJids = selectedUsers.map(id => formatUserIdToJid(id));
         await createGroup(groupName, memberJids, groupImage);
         dispatch(navigateLeftStack({ activeStack: 'chats', subStack: '' }));
         showSuccessToast(t('toast.GROUP_CREATED_SUCCESS'));
      } catch (err) {
         logger.error('handleCreateGroup failed:', err);
         showErrorToast(err?.message || t('toast.GROUP_CREATE_FAILED'));
      } finally {
         setIsSubmitting(false);
      }
   }, [selectedCount, minCount, selectedUsers, groupName, groupImage, dispatch, t]);

   const handleAddParticipants = useCallback(async () => {
      if (blockOfflineAction()) return;
      if (selectedCount === 0 || isSubmitting || !groupId) return;

      setIsSubmitting(true);
      try {
         const groupJid = formatUserIdToJid(groupId, 'groupchat');
         const memberJids = selectedUsers.map(id => formatUserIdToJid(id));
         const groupName = getUserNickName(groupId);
         const res = await addParticipants(groupJid, groupName, memberJids);

         if (res?.statusCode === 200) {
            const currentParticipants = store.getState().groupParticipantsData.participants?.[groupId] || [];
            const newParticipants = selectedUsers.map(userId => ({
               userId,
               userType: 'n',
            }));

            dispatch(
               setGroupParticipants({
                  groupId,
                  participants: [...currentParticipants, ...newParticipants],
               }),
            );

            showSuccessToast(
               selectedCount === 1
                  ? t('toast.PARTICIPANT_ADDED_SUCCESS')
                  : t('toast.PARTICIPANTS_ADDED_SUCCESS', { selectedCount }),
            );
            onClose?.();
         } else {
            showErrorToast(res?.message || t('toast.PARTICIPANTS_ADD_FAILED'));
         }
      } catch (err) {
         logger.error('handleAddParticipants failed:', err);
         showErrorToast(err?.message || t('toast.SOMETHING_WENT_WRONG_RETRY'));
      } finally {
         setIsSubmitting(false);
      }
   }, [selectedUsers, selectedCount, isSubmitting, groupId, onClose, dispatch, t]);

   const handleSubmit = isCreateMode ? handleCreateGroup : handleAddParticipants;

   const isSubmitDisabled =
      isSubmitting || (isCreateMode ? selectedCount < (minCount || config.minGroupMembers) : selectedCount === 0);

   return {
      selectedUsers,
      selectedCount,
      isSubmitting,
      handleToggleUser,
      handleSubmit,
      isSubmitDisabled,
      isCreateMode,
   };
};
